/**
 * Capture console.* → /api/audit/ingest/logs (token stays on backend).
 * Soft-fail if audit disabled.
 */

import { API_CONFIG } from '@/config/api'

type Level = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR'

interface QueuedLog {
  message: string
  level: Level
  stream: string
  source: string
  time: string
}

const MAX_BATCH = 40
const FLUSH_MS = 2500

let queue: QueuedLog[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let enabled: boolean | null = null
let hooked = false

function apiRoot(): string {
  return `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`
}

function serialize(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a
      try {
        return JSON.stringify(a)
      } catch {
        return String(a)
      }
    })
    .join(' ')
    .slice(0, 4000)
}

function enqueue(level: Level, args: unknown[]) {
  queue.push({
    message: serialize(args),
    level,
    stream: 'browser',
    source: 'frontend',
    time: new Date().toISOString(),
  })
  if (queue.length >= MAX_BATCH) {
    void flush()
  } else if (!timer) {
    timer = setTimeout(() => {
      timer = null
      void flush()
    }, FLUSH_MS)
  }
}

async function ensureEnabled(): Promise<boolean> {
  if (enabled !== null) return enabled
  try {
    const res = await fetch(`${apiRoot()}/audit/status`)
    if (!res.ok) {
      enabled = false
      return false
    }
    const data = (await res.json()) as { enabled?: boolean }
    enabled = Boolean(data.enabled)
  } catch {
    enabled = false
  }
  return enabled
}

async function flush(): Promise<void> {
  if (!queue.length) return
  const batch = queue.splice(0, queue.length)
  if (!(await ensureEnabled())) return
  try {
    await fetch(`${apiRoot()}/audit/ingest/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logs: batch }),
      keepalive: true,
    })
  } catch {
    /* soft-fail */
  }
}

export function initQolaigAudit(): void {
  if (hooked || typeof window === 'undefined') return
  hooked = true

  const levels: { method: 'log' | 'info' | 'warn' | 'error' | 'debug'; level: Level }[] = [
    { method: 'log', level: 'INFO' },
    { method: 'info', level: 'INFO' },
    { method: 'warn', level: 'WARN' },
    { method: 'error', level: 'ERROR' },
    { method: 'debug', level: 'DEBUG' },
  ]

  for (const { method, level } of levels) {
    const original = console[method].bind(console)
    console[method] = (...args: unknown[]) => {
      try {
        enqueue(level, args)
      } catch {
        /* ignore */
      }
      original(...args)
    }
  }

  window.addEventListener('error', (ev) => {
    enqueue('ERROR', [ev.message, ev.filename, ev.lineno])
  })
  window.addEventListener('unhandledrejection', (ev) => {
    enqueue('ERROR', ['unhandledrejection', String(ev.reason)])
  })
  window.addEventListener('pagehide', () => {
    void flush()
  })

  void ensureEnabled()
}
